import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { noop } from '@rivethealth/core/util';
import { merge, of, Subject, Unsubscribable } from 'rxjs';
import { exhaustMap, take, tap } from 'rxjs/operators';
import { ContentService } from '../core/content.service';
import { runInZone } from '../core/zone';
import { async } from '../rx/util';

/**
 * Animate the size of the element.
 *
 * If the element has content, call check() when the content changes.
 * Unfotunately, this directive cannot itself add that hook.
 * https://github.com/angular/angular/issues/27877
 */
@Directive({ selector: '[animatedSize]' })
export class AnimatedSizeDirective implements OnDestroy {
  private lastHeight: number;
  private lastWidth: number;
  private scheduleCheckSubscription: Unsubscribable;
  private timeout: ReturnType<typeof setTimeout> | undefined;

  private readonly doScheduleCheck = new Subject();

  // scheduled, due to animations
  // https://github.com/angular/angular/issues/27917
  private readonly scheduleCheck_ = merge(
    this.contentService.observe(this.element.nativeElement),
    this.doScheduleCheck,
  ).pipe(
    exhaustMap(() =>
      this.zone.isStable ? of(undefined) : this.zone.onStable.pipe(take(1)),
    ),
    runInZone(this.zone),
    async(),
    tap(() => this.check()),
  );

  constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly contentService: ContentService,
    private readonly zone: NgZone,
  ) {}

  @Input() animationDuration = 150;

  @Input() animateWidth = true;

  @Input() animateHeight = true;

  private _animateWidth = true;
  private _animateHeight = true;

  /**
   * Because of when this is called and how it measures, it manually and
   * synchronously manipulates the DOM.
   */
  private check() {
    // since the animation is short, allow it to finish before starting another
    if (this.timeout === undefined) {
      const { clientHeight: height, clientWidth: width } =
        this.element.nativeElement;
      const heightChanged = height && height !== this.lastHeight;
      const widthChanged = width && width !== this.lastWidth;
      if (heightChanged || widthChanged) {
        if (this._animateHeight) {
          this.element.nativeElement.classList.add('animating-height');
          this.element.nativeElement.style.height = `${this.lastHeight}px`;
        }
        if (this._animateWidth) {
          this.element.nativeElement.classList.add('animating-width');
          this.element.nativeElement.style.width = `${this.lastWidth}px`;
        }
        noop(this.element.nativeElement.offsetHeight); // force reflow
        this.element.nativeElement.style.transition = `all ${this.animationDuration}ms`;
        if (this._animateHeight) {
          this.element.nativeElement.style.height = `${height}px`;
        }
        if (this._animateWidth) {
          this.element.nativeElement.style.width = `${width}px`;
        }
        this.timeout = setTimeout(() => {
          this.reset();
          this.check();
        }, this.animationDuration);
      }
      this.lastHeight = height;
      this.lastWidth = width;
    }
  }

  private reset() {
    this.timeout = undefined;
    this.element.nativeElement.classList.remove(
      'animating-height',
      'animating-width',
    );
    if (this._animateHeight) {
      this.element.nativeElement.style.height = '';
    }
    if (this._animateWidth) {
      this.element.nativeElement.style.width = '';
    }
    this.element.nativeElement.style.transition = '';
  }

  ngOnDestroy() {
    if (this.scheduleCheckSubscription != null) {
      this.scheduleCheckSubscription.unsubscribe();
    }
    if (this.timeout !== undefined) {
      clearTimeout(this.timeout);
    }
  }

  ngOnInit() {
    this._animateWidth =
      this.animateWidth && !this.element.nativeElement.style.width;
    this._animateHeight =
      this.animateHeight && !this.element.nativeElement.style.height;

    this.scheduleCheckSubscription = this.zone.runOutsideAngular(() =>
      this.scheduleCheck_.subscribe(),
    );
  }

  @HostListener('window:resize')
  resize() {
    if (this.timeout === undefined) {
      // if not animating, do not animate
      const { clientHeight: height, clientWidth: width } =
        this.element.nativeElement;
      this.lastHeight = height;
      this.lastWidth = width;
    } else {
      // if animating, replace animation in progress
      clearTimeout(this.timeout);
      this.reset();
      this.zone.runOutsideAngular(() => this.doScheduleCheck.next(undefined));
    }
  }
}
